import React, {ReactNode, useEffect, useState} from 'react';
import {DetailGrid, Utility} from '@/components/GridWidget';
import {WdiCardTitle, wdiCore, WdiModalAppResult, WdiModalProps} from '@/components/WdiControls';
import {Button, Dropdown, Input, Menu, message} from 'antd';
import {TableColumnRanderFormats} from '@/components/WdiBus/TableColumnRanderFormats';
import {NsGrid} from '@/components/GridWidget/GridDefProps';
import DataStore from '@/components/GridWidget/DataStore';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {WdiInputNumber} from '@/components/WdiControls/WdiInputNumber';
import {ImportModalApp} from '@/components/WdiControls/WdiImport';
import {ResponseStatus} from '@/utils/request';
import FormInvoiceEditApp from './FormInvoiceEdit';
import QuoteInvoiceApp from './QuoteInvoice';
import {Enum} from '@/enums/system';
import {GridApi} from '@/services/gridapi';
import {Invoices} from "@/services/sales/financial/invoices";
import {InvoiceIdentifyApp} from "@/pages/financial/Invoices/components/InvoiceIdentify";
import {InvoiceQueryApp} from "@/pages/financial/Invoices/components/InvoiceQuery";
import {InvoiceCreateForApplyApp} from "@/pages/financial/Invoices/components/InvoiceCreate";

const ModalAppTypeEnums = {
  None: 0,
  Insert: 1,
  Edit: 2,
  Import: 3,
  Select: 4,
  Identify: 5,
  Create: 6,
  QueryCreate: 7,
};

export type DetailForApplyProps = {
  data?: any,
  dataSource: any[],
  onChange?: (dataSource: any) => void;
}

const vatTypeValidate = (vatType?: any) => {
  if (!vatType || wdiCore.Object.IsNullOrUndefined(vatType.vatTypeId)) {
    message.error("请先选择税率").then();
    return false
  }
  return true;
};

const FormInvoiceForApplyPanel: React.FC<DetailForApplyProps> = (props) => {
  /// Grid初始化配置，GridSettings请求完数据之后再进行加载
  const [isLoaded, setIsLoaded] = useState(false);
  const [gridSettings, setGridSettings] = useState<any>({});
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false});
  const [queryModalProps, setQueryModalProps] = useState<WdiModalProps>({visible: false});
  const [queryInvoice, setQueryInvoice] = useState<API.Invoice>();
  const {data, dataSource, onChange} = props;

  const onClose = () => {
    setModalProps({visible: false, title: "", type: ModalAppTypeEnums.None, id: null});
  };

  const onQueryClose = () => {
    setQueryModalProps({visible: false});
  };

  const onEditSave = (result?: WdiModalAppResult) => {
    if (result && result.code === ResponseStatus.SUCCESS) {
      onChange?.(DataStore.add(dataSource, Object.assign({}, {
        amountNotPaid: result.data.amountInVat,
        taxNotPaid: result.data.tax
      }, {...result.data})));
    }
    onClose();
  };

  const onImportSave = (uploadResult?: WdiModalAppResult) => {
    if (uploadResult?.code == ResponseStatus.SUCCESS) {
      const result = uploadResult?.data?.data;
      const addDataSource: any[] = [];
      result?.forEach((item: any) => {
        addDataSource.push(Object.assign({}, {amountNotPaid: item.amountInVat, taxNotPaid: item.tax}, {...item}));
      })
      onChange?.(DataStore.addMulti(dataSource, addDataSource));
    }
    onClose();
  }

  const onSelectSave = (selectResult?: WdiModalAppResult) => {
    const result = selectResult?.data;
    if (result && result.length > 0) {
      let newResult: any[] = [];
      result.forEach((item: any) => {
        if ((dataSource || []).findIndex((record) => record.invoiceCode == item.invoiceCode && record.invoiceNo == item.invoiceNo) < 0) {
          newResult.push({...item, usingAmount: 0, usingTax: 0, remark: ""});
        }
      })
      if (newResult.length > 0) {
        onChange?.(DataStore.addMulti(dataSource, newResult));
      }
    }
    onClose();
  }

  const onIdentifySave = (result?: WdiModalAppResult) => {
    setModalProps({
      visible: true,
      title: "发票创建",
      type: ModalAppTypeEnums.Create,
      data: Object.assign({invoices: result?.data}, {...data})
    });
  }

  const onCreateSave = (result?: WdiModalAppResult) => {
    const newResult: any[] = [];
    result?.data.forEach((item: any) => {
      if (item.taxRate == data.vatType.vatTypeValue) {
        newResult.push(Object.assign({amountNotPaid: item.amountInVat, taxNotPaid: item.tax}, {...item}));
      }
    })
    onChange?.(DataStore.addMulti(dataSource, newResult));
    if (modalProps.type == ModalAppTypeEnums.QueryCreate && newResult.length > 0) {
      setQueryInvoice({
        invoiceId: newResult[0].invoiceId,
      })
    }
    onClose();
  }

  useEffect(() => {
    GridApi.searchConfig({id: 'FormInvoice', allowModifyColumns: false}).then((response) => {
      const settings: any = Object.assign({}, response.data, {
        actionsAlign: NsGrid.ActionsAlign.Left,
        showAggr: true,
      });

      settings.formSchema.allFields.remark.getDisplayFormater = (_: any, record: any) => {
        return record.remarkFormInvoice;
      };

      settings.formSchema.allFields.amountInVat.aggr = TableColumnRanderFormats.sumAggr('amountInVat', 2);
      settings.formSchema.allFields.tax.aggr = TableColumnRanderFormats.sumAggr('tax', 2);
      settings.formSchema.allFields.amountNotPaid.aggr = TableColumnRanderFormats.sumAggr('amountNotPaid', 2);
      settings.formSchema.allFields.taxNotPaid.aggr = TableColumnRanderFormats.sumAggr('taxNotPaid', 2);
      settings.formSchema.allFields.usingAmount.aggr = TableColumnRanderFormats.sumAggr('usingAmount', 2);
      settings.formSchema.allFields.usingTax.aggr = TableColumnRanderFormats.sumAggr('usingTax', 2);

      settings.formSchema.allFields.invoiceCode.width = 180;
      settings.formSchema.allFields.invoiceNo.width = 180;
      settings.formSchema.allFields.invoiceDate.width = 110;
      settings.formSchema.allFields.amountInVat.width = 140;
      settings.formSchema.allFields.tax.width = 100;
      settings.formSchema.allFields.amountNotPaid.width = 160;
      settings.formSchema.allFields.taxNotPaid.width = 120;
      settings.formSchema.allFields.usingAmount.width = 160;
      settings.formSchema.allFields.usingTax.width = 120;

      setGridSettings(settings);
      setIsLoaded(true);
    });
  }, []);

  const pageGridSettings = Object.assign({}, gridSettings);
  pageGridSettings.antdProps = {
    dataSource,
    pagination: false
  };
  if (pageGridSettings && pageGridSettings.formSchema && pageGridSettings.formSchema.actions) {
    Utility.setActionCallback(
      pageGridSettings.formSchema.actions,
      'SystemDelete',
      async (_grid: any, _action: any, record: any) => {
        if (confirm("确定要删除此发票信息吗？")) {
          Invoices.deleteFormInvoice(record.invoiceId, record.formInvoiceId).then(responseResult => {
            if (responseResult.code == ResponseStatus.SUCCESS) {
              onChange?.(DataStore.del(dataSource, record.uid));
              message.success("删除成功");
            } else {
              message.error(responseResult.message);
            }
          })

        }
      },
    );
  }

  const editColumn = {
    usingAmount: (_t: any, record: any) => {
      return <WdiInputNumber value={record.usingAmount} onBlur={(e: any) => {
        const {...newRecord} = record;
        newRecord.usingAmount = wdiCore.Number.ToNumber(e.target.value, 2);
        onChange?.(DataStore.edit(dataSource, newRecord));
      }}/>
    },
    usingTax: (_t: any, record: any) => {
      return <WdiInputNumber value={record.usingTax} onBlur={(e: any) => {
        const {...newRecord} = record;
        newRecord.usingTax = wdiCore.Number.ToNumber(e.target.value, 2);
        onChange?.(DataStore.edit(dataSource, newRecord));
      }}/>
    },
    remark: (_t: any, record: any) => {
      return <Input defaultValue={record.remarkFormInvoice} onBlur={(e: any) => {
        const {...newRecord} = record;
        newRecord.remark = e.target.value;
        onChange?.(DataStore.edit(dataSource, newRecord));
      }}/>;
    }
  };

  return (
    <>
      <WdiPanel
        title={<WdiCardTitle iconType={Enum.IconType.INVOICE} title='发票信息'/>}
        extra={
          <>
            <Button type='primary' onClick={() => {
              let totalAmount = 0.00, totalTax = 0.00;
              if (data.details && data.details.length > 0) {
                data.details.forEach((item: any) => {
                  totalAmount += wdiCore.Number.ToNumber(item.amountInVat);
                  totalTax += wdiCore.Number.ToNumber(item.tax);
                })
              }
              const newDataSource = [...dataSource];
              newDataSource.forEach((item, rowIndex) => {
                item.usingAmount = 0;
                let usedAmount = 0, usedTax = 0;
                if (totalAmount > 0) {
                  if (rowIndex == (newDataSource.length - 1)) {
                    usedAmount = totalAmount;
                    usedTax = totalTax;
                  } else {
                    if (totalAmount >= item.amountNotPaid) {
                      usedAmount = item.amountNotPaid;
                      totalAmount -= item.amountNotPaid
                      usedTax = item.taxNotPaid;
                      totalTax -= item.taxNotPaid;
                    } else {
                      usedAmount = totalAmount;
                      totalAmount = 0;
                      usedTax = totalTax;
                      totalTax = 0;
                    }
                  }
                  item.usingAmount = wdiCore.Number.ToNumber(usedAmount, 2);
                  item.usingTax = wdiCore.Number.ToNumber(usedTax, 2);
                }
              })
              onChange?.(newDataSource);
            }} style={{float: 'right', marginRight: 10}}>
              金额匹配
            </Button>
            <Button type='primary' onClick={() => {
              if (vatTypeValidate(data.vatType)) {
                setModalProps({
                  visible: true,
                  title: '引用已有发票（请注意核销单税率填写是否正确）',
                  type: ModalAppTypeEnums.Select,
                  id: null,
                  data: {...data}
                });
              }
            }} style={{float: 'right', marginRight: 10}}>
              引用原发票
            </Button>

            <Dropdown.Button
              buttonsRender={(buttons: ReactNode[]) => {
                return [buttons[0], <Button type={"primary"} style={{width: 50, fontSize: 12}}>更多</Button>]
              }}
              type='primary'
              onClick={() => {
                if (vatTypeValidate(data.vatType)) {
                  setModalProps({
                    visible: true,
                    title: '新建发票信息',
                    type: ModalAppTypeEnums.Insert,
                    id: null,
                    data: {
                      ...data
                    }
                  });
                }
              }}
              overlay={
                <Menu items={[
                  {
                    key: 'import',
                    label: '发票新建导入',
                    onClick: () => {
                      if (vatTypeValidate(data.vatType)) {
                        setModalProps({
                          visible: true,
                          title: '发票导入',
                          type: ModalAppTypeEnums.Import,
                          id: null,
                          data: {
                            ...data
                          }
                        });
                      }
                    }
                  }]}/>
              }
              style={{float: 'right'}}
            >发票新建</Dropdown.Button>
          </>
        }
      >
        {
          isLoaded &&
          modalProps.visible &&
          (modalProps.type === ModalAppTypeEnums.Import) &&
          <ImportModalApp
            title={modalProps.title}
            visible={modalProps.visible}
            settingsUrl="/expenseapi/invoice/getApplyImportSettings"
            uploadingUrl="/expenseapi/invoice/applyImportUploading"
            onClose={onClose}
            onSave={onImportSave}
            clientParameters={{
              soldToCode: data?.soldToCode,
              supplierId: data?.supplierId,
              vatTypeId: data?.vatType.vatTypeId,
              companyCode: data?.companyCode,
              paymentMethodId: data?.paymentMethodId,
              payToUserId: data?.payToUserId,
            }}
          />
        }
        {isLoaded &&
          modalProps.visible &&
          modalProps.type === ModalAppTypeEnums.Select &&
          <QuoteInvoiceApp
            {...modalProps}
            onClose={onClose}
            onSave={onSelectSave}
          />
        }
        {
          isLoaded &&
          modalProps.visible &&
          modalProps.type === ModalAppTypeEnums.Identify &&
          <InvoiceIdentifyApp
            {...modalProps}
            onClose={onClose}
            onSave={onIdentifySave}
          />
        }
        {
          isLoaded &&
          modalProps.visible &&
          (modalProps.type === ModalAppTypeEnums.Create || modalProps.type === ModalAppTypeEnums.QueryCreate) &&
          <InvoiceCreateForApplyApp
            {...modalProps}
            onClose={onClose}
            onSave={onCreateSave}
          />
        }
        {
          isLoaded &&
          queryModalProps.visible &&
          <InvoiceQueryApp
            {...queryInvoice}
            {...queryModalProps}
            onClose={onQueryClose}
          />
        }
        {
          isLoaded &&
          modalProps.visible &&
          (modalProps.type === ModalAppTypeEnums.Insert || modalProps.type === ModalAppTypeEnums.Edit) &&
          <FormInvoiceEditApp
            {...modalProps}
            onClose={onClose}
            onSave={onEditSave}
          />
        }
        {isLoaded && <DetailGrid {...pageGridSettings} params={data} editColumn={editColumn}/>}
      </WdiPanel>
    </>
  );
}
export default FormInvoiceForApplyPanel;

export const FormInvoiceForApprovalPanel: React.FC<DetailForApplyProps> = (props) => {
  /// Grid初始化配置，GridSettings请求完数据之后再进行加载
  const [isLoaded, setIsLoaded] = useState(false);
  const [gridSettings, setGridSettings] = useState<any>({});
  const {dataSource} = props;

  useEffect(() => {
    GridApi.searchConfig({id: 'FormInvoiceForApproval', allowModifyColumns: false}).then((response) => {
      const settings: any = Object.assign({}, response.data, {
        actionsAlign: NsGrid.ActionsAlign.None,
        showAggr: true,
      });
      settings.formSchema.allFields.remark.getDisplayFormater = (_text: any, record: any) => {
        return record.remarkFormInvoice;
      };
      settings.formSchema.allFields.amountInVat.aggr = TableColumnRanderFormats.sumAggr('amountInVat', 2);
      settings.formSchema.allFields.tax.aggr = TableColumnRanderFormats.sumAggr('tax', 2);
      settings.formSchema.allFields.amountNotPaid.aggr = TableColumnRanderFormats.sumAggr('amountNotPaid', 2);
      settings.formSchema.allFields.taxNotPaid.aggr = TableColumnRanderFormats.sumAggr('taxNotPaid', 2);
      settings.formSchema.allFields.usingAmount.aggr = TableColumnRanderFormats.sumAggr('usingAmount', 2);
      settings.formSchema.allFields.usingTax.aggr = TableColumnRanderFormats.sumAggr('usingTax', 2);

      settings.formSchema.allFields.invoiceCode.width = 180;
      settings.formSchema.allFields.invoiceNo.width = 180;
      settings.formSchema.allFields.invoiceDate.width = 110;
      settings.formSchema.allFields.amountInVat.width = 140;
      settings.formSchema.allFields.tax.width = 100;
      settings.formSchema.allFields.amountNotPaid.width = 160;
      settings.formSchema.allFields.taxNotPaid.width = 120;
      settings.formSchema.allFields.usingAmount.width = 160;
      settings.formSchema.allFields.usingTax.width = 120;

      setGridSettings(settings);
      setIsLoaded(true);
    });
  }, []);

  const pageGridSettings = Object.assign({}, gridSettings);
  pageGridSettings.antdProps = {
    dataSource,
    pagination: false
  };

  return (
    <>
      <WdiPanel title={<WdiCardTitle iconType={Enum.IconType.INVOICE} title='发票信息'/>} collapsed={true}>
        {isLoaded && <DetailGrid {...pageGridSettings} />}
      </WdiPanel>
    </>
  );
}
